<template>
  <div class="tab-content">
    <!-- Identidade -->
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
        </div>
        <div>
          <h3 class="card-title">Identidade</h3>
          <p class="card-desc">Nome exibido e endereço de acesso do sistema.</p>
        </div>
      </div>
      <div class="fields-row">
        <div class="field-wrap">
          <label class="field-label" for="sys-name">Nome do sistema</label>
          <input id="sys-name" class="field-input" :value="form.siteName" type="text" placeholder="Ex: Obra Torre A"
            @input="$emit('update-form', { field: 'siteName', value: ($event.target as HTMLInputElement).value })" />
        </div>
        <div class="field-wrap">
          <label class="field-label" for="sys-username">Username da obra</label>
          <div class="username-row">
            <input id="sys-username" class="field-input username-input" :value="form.systemUsername" type="text"
              :disabled="!usernameEditEnabled" placeholder="somente letras minúsculas"
              @input="onSystemUsernameInput(($event.target as HTMLInputElement).value)" />
            <template v-if="usernameEditEnabled">
              <button class="inline-btn inline-btn--save" type="button" @click="saveSystemUsername">Salvar</button>
              <button class="inline-btn inline-btn--cancel" type="button" @click="cancelSystemUsernameEdit">Cancelar</button>
            </template>
            <button v-else class="inline-btn" type="button" :disabled="usernameActionDisabled" @click="enableSystemUsernameEdit">Alterar</button>
          </div>
          <p class="field-hint">Troca permitida a cada 30 dias · última troca: <strong>{{ lastUsernameChangeText }}</strong></p>
          <p v-if="usernameCooldownMessage" class="field-hint field-hint--warn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ usernameCooldownMessage }}
          </p>
        </div>
      </div>
    </section>

    <!-- Aparência -->
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
        </div>
        <div>
          <h3 class="card-title">Aparência</h3>
          <p class="card-desc">Logo e imagem de fundo da tela de login.</p>
        </div>
      </div>
      <div class="media-grid">
        <div class="media-block">
          <div class="media-label-row">
            <span class="field-label">Logo</span>
            <span class="field-hint" style="margin:0">Ideal: 180×40 px</span>
          </div>
          <div class="upload-zone" :class="{ 'upload-zone--has': form.siteLogoUrl }" @click="triggerLogoInput">
            <img v-if="form.siteLogoUrl" class="upload-preview" :src="$resolveUrl(form.siteLogoUrl)" alt="logo" />
            <template v-else>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <span class="upload-hint">Clique para enviar</span>
            </template>
          </div>
          <button v-if="form.siteLogoUrl" class="remove-btn" type="button" @click="$emit('update-form', { field: 'siteLogoUrl', value: '' })">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
            Remover logo
          </button>
          <input ref="logoInputRef" class="hidden-input" type="file" accept="image/*" @change="handleLogoUpload" />
        </div>
        <div class="media-block">
          <div class="media-label-row">
            <span class="field-label">Plano de fundo do login</span>
            <label class="toggle-check">
              <input :checked="form.usePrimaryLoginBackground" type="checkbox" @change="$emit('update-form', { field: 'usePrimaryLoginBackground', value: ($event.target as HTMLInputElement).checked ? 'true' : 'false' })" />
              Usar cor primária
            </label>
          </div>
          <div class="upload-zone" :class="{ 'upload-zone--has': form.loginBackgroundUrl && !form.usePrimaryLoginBackground, 'upload-zone--disabled': form.usePrimaryLoginBackground }"
            @click="!form.usePrimaryLoginBackground && backgroundInputRef?.click()">
            <img v-if="form.loginBackgroundUrl && !form.usePrimaryLoginBackground" class="upload-preview" :src="$resolveUrl(form.loginBackgroundUrl)" alt="fundo" />
            <template v-else-if="form.usePrimaryLoginBackground">
              <div class="color-swatch-preview" :style="{ background: pickerHex(form.colors.primary) }"></div>
              <span class="upload-hint">Usando cor primária</span>
            </template>
            <template v-else>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <span class="upload-hint">Clique para enviar</span>
            </template>
          </div>
          <button v-if="form.loginBackgroundUrl && !form.usePrimaryLoginBackground" class="remove-btn" type="button"
            @click="$emit('update-form', { field: 'loginBackgroundUrl', value: '' })">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
            Remover fundo
          </button>
          <input ref="backgroundInputRef" class="hidden-input" type="file" accept="image/*" :disabled="form.usePrimaryLoginBackground" @change="handleBackgroundUpload" />
        </div>
      </div>
    </section>

    <!-- Cores da interface -->
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="13.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="10.5" r="2.5"/><circle cx="8.5" cy="7.5" r="2.5"/><circle cx="6.5" cy="12.5" r="2.5"/><path d="M12 20c4.4 0 8-3.6 8-8h-8v8z"/><path d="M4 12c0 4.4 3.6 8 8 8v-8H4z"/></svg>
        </div>
        <div>
          <h3 class="card-title">Cores da interface</h3>
          <p class="card-desc">Personalização visual do sistema (sidebar, navbar, botões).</p>
        </div>
      </div>
      <div class="color-grid">
        <div class="color-block">
          <div class="color-swatch" :style="{ background: pickerHex(form.colors.primary) }"></div>
          <div class="color-fields">
            <label class="field-label" for="c-primary">Cor primária</label>
            <div class="color-input-row">
              <input id="c-primary" class="field-input hex-input" :value="form.colors.primary" type="text" placeholder="#0f4c81"
                @input="$emit('update-form', { field: 'colors.primary', value: ($event.target as HTMLInputElement).value })" />
              <input class="picker-inside" :value="pickerHex(form.colors.primary)" type="color"
                @input="$emit('update-form', { field: 'colors.primary', value: normalizeHex(($event.target as HTMLInputElement).value) })" />
            </div>
          </div>
        </div>
        <div class="color-block">
          <div class="color-swatch" :style="{ background: form.autoSecondary ? 'linear-gradient(135deg,#ccc 50%,#eee 50%)' : pickerHex(form.colors.secondary) }"></div>
          <div class="color-fields">
            <div class="color-label-row">
              <label class="field-label" for="c-secondary">Cor secundária</label>
              <label class="toggle-check">
                <input :checked="form.autoSecondary" type="checkbox"
                  @change="$emit('update-form', { field: 'autoSecondary', value: ($event.target as HTMLInputElement).checked ? 'true' : 'false' })" />
                Automática
              </label>
            </div>
            <div class="color-input-row">
              <input id="c-secondary" class="field-input hex-input" :value="form.colors.secondary" type="text" :disabled="form.autoSecondary" placeholder="#e1a12f"
                @input="$emit('update-form', { field: 'colors.secondary', value: ($event.target as HTMLInputElement).value })" />
              <input class="picker-inside" :value="pickerHex(form.colors.secondary)" type="color" :disabled="form.autoSecondary"
                @input="$emit('update-form', { field: 'colors.secondary', value: normalizeHex(($event.target as HTMLInputElement).value) })" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Relatórios -->
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        </div>
        <div>
          <h3 class="card-title">Relatórios de Planejamento</h3>
          <p class="card-desc">Escolha como os relatórios de obra são gerados a partir do Gantt.</p>
        </div>
      </div>
      <div class="report-mode-options">
        <label class="report-mode-card" :class="{ 'report-mode-card--active': (form.ganttReportMode || 'ia') === 'ia' }">
          <input type="radio" name="ganttReportMode" value="ia" :checked="(form.ganttReportMode || 'ia') === 'ia'"
            @change="$emit('update-form', { field: 'ganttReportMode', value: 'ia' })" />
          <div class="report-mode-body">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z"/><path d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14z" opacity=".5"/></svg>
            <div>
              <strong>Modo IA</strong>
              <span>EstagIArio Joe redige e edita relatórios com linguagem natural.</span>
            </div>
          </div>
        </label>
        <label class="report-mode-card" :class="{ 'report-mode-card--active': form.ganttReportMode === 'offline' }">
          <input type="radio" name="ganttReportMode" value="offline" :checked="form.ganttReportMode === 'offline'"
            @change="$emit('update-form', { field: 'ganttReportMode', value: 'offline' })" />
          <div class="report-mode-body">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
            <div>
              <strong>Modo Offline</strong>
              <span>Relatório gerado automaticamente a partir do Gantt, sem IA, funciona sem internet.</span>
            </div>
          </div>
        </label>
      </div>
    </section>

    <div class="save-row">
      <button class="btn-save" type="button" @click="$emit('save')">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
        Salvar configurações
      </button>
      <Transition name="fade"><span v-if="successText" class="save-success"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>{{ successText }}</span></Transition>
      <p v-if="logoError" class="save-error">{{ logoError }}</p>
      <p v-if="errorText" class="save-error">{{ errorText }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from 'vue'
import type { SystemConfig } from '@/store/system'
import { compressImage } from '@/utils/imageCompress'

export default defineComponent({
  name: 'SystemTabGeral',
  props: {
    form: { type: Object as PropType<SystemConfig>, required: true },
    errorText: { type: String, default: '' },
    successText: { type: String, default: '' },
  },
  emits: ['update-form', 'save'],
  setup(props, { emit }) {
    const logoError = ref('')
    const logoInputRef = ref<HTMLInputElement | null>(null)
    const backgroundInputRef = ref<HTMLInputElement | null>(null)
    const usernameEditEnabled = ref(false)
    const usernameBeforeEdit = ref('')

    const normalizeHex = (v: string) => { const s = `${v||''}`.trim(); const h = s.startsWith('#') ? s : `#${s}`; return /^#[0-9A-Fa-f]{6}$/.test(h) ? h : h.slice(0,7) }
    const pickerHex = (v: string) => { const s = `${v||''}`.trim(); const h = s.startsWith('#') ? s : `#${s}`; return /^#[0-9A-Fa-f]{6}$/.test(h) ? h : '#4f3300' }

    const lastUsernameChangeDate = computed(() => {
      if (!props.form.systemUsernameChangedAt) return null
      const d = new Date(props.form.systemUsernameChangedAt)
      return isNaN(d.getTime()) ? null : d
    })
    const fmtDate = (d: Date) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`
    const nextAllowedDate = computed(() => { if (!lastUsernameChangeDate.value) return null; const d = new Date(lastUsernameChangeDate.value); d.setDate(d.getDate()+30); return d })
    const canChangeNow = computed(() => !nextAllowedDate.value || new Date() >= nextAllowedDate.value)
    const lastUsernameChangeText = computed(() => lastUsernameChangeDate.value ? fmtDate(lastUsernameChangeDate.value) : 'nunca')
    const usernameCooldownMessage = computed(() => canChangeNow.value || !nextAllowedDate.value ? '' : `Você trocou em ${lastUsernameChangeText.value}, pode trocar a partir de ${fmtDate(nextAllowedDate.value)}.`)
    const usernameActionDisabled = computed(() => !usernameEditEnabled.value && !canChangeNow.value)

    const onSystemUsernameInput = (v: string) => emit('update-form', { field: 'systemUsername', value: String(v||'').toLowerCase().replace(/[^a-z]/g,'') })
    const enableSystemUsernameEdit = () => { if (!canChangeNow.value) return; usernameBeforeEdit.value = props.form.systemUsername||''; usernameEditEnabled.value = true }
    const saveSystemUsername = () => { usernameEditEnabled.value = false; emit('save') }
    const cancelSystemUsernameEdit = () => { emit('update-form', { field: 'systemUsername', value: usernameBeforeEdit.value }); usernameEditEnabled.value = false }

    const triggerLogoInput = () => logoInputRef.value?.click()

    const handleLogoUpload = async (e: Event) => {
      logoError.value = ''
      const file = (e.target as HTMLInputElement).files?.[0]
      ;(e.target as HTMLInputElement).value = ''
      if (!file) return
      try { emit('update-form', { field: 'siteLogoUrl', value: await compressImage(file, 512, 512) }) }
      catch { logoError.value = 'Falha ao processar logo.' }
    }

    const handleBackgroundUpload = async (e: Event) => {
      logoError.value = ''
      const file = (e.target as HTMLInputElement).files?.[0]
      ;(e.target as HTMLInputElement).value = ''
      if (!file) return
      try { emit('update-form', { field: 'loginBackgroundUrl', value: await compressImage(file, 1920, 1080) }) }
      catch { logoError.value = 'Falha ao processar imagem de fundo.' }
    }

    return {
      logoError, logoInputRef, backgroundInputRef, usernameEditEnabled, usernameActionDisabled,
      lastUsernameChangeText, usernameCooldownMessage, pickerHex, normalizeHex,
      onSystemUsernameInput, enableSystemUsernameEdit, saveSystemUsername, cancelSystemUsernameEdit,
      triggerLogoInput, handleLogoUpload, handleBackgroundUpload,
    }
  }
})
</script>

<style scoped lang="scss">
.tab-content { display: flex; flex-direction: column; gap: 16px; }
.settings-card { border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card,#fff); padding: 22px 24px; display: flex; flex-direction: column; gap: 20px; }
.card-header { display: flex; align-items: flex-start; gap: 14px; padding-bottom: 16px; border-bottom: 1px solid rgba(31,30,26,.07); }
.card-icon { width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0; background: rgba(31,30,26,.06); display: flex; align-items: center; justify-content: center; color: var(--ink,#2c2b27); }
.card-title { margin: 0 0 2px; font-size: 15px; font-weight: 700; color: var(--ink,#2c2b27); }
.card-desc { margin: 0; font-size: 12px; color: var(--ink-muted,#888); }
.fields-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.field-wrap { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 11px; font-weight: 700; color: var(--ink-muted,#888); text-transform: uppercase; letter-spacing: .05em; }
.field-input { padding: 9px 12px; border-radius: 8px; border: 1.5px solid rgba(31,30,26,.16); font-size: 14px; color: var(--ink,#2c2b27); background: #fff; outline: none; box-sizing: border-box; width: 100%; transition: border-color .15s; &:focus { border-color: var(--system-navbar,#0f4c81); } &:disabled { background: #f0f0f0; color: #999; cursor: not-allowed; } }
.field-hint { font-size: 11px; color: var(--ink-muted,#aaa); margin: 0; &--warn { color: #c2410c; } }
.username-row { display: flex; gap: 6px; align-items: stretch; }
.username-input { flex: 1; }
.inline-btn { flex-shrink: 0; padding: 0 14px; border-radius: 8px; border: 1.5px solid rgba(31,30,26,.18); background: #fff; cursor: pointer; font-size: 12px; font-weight: 600; color: var(--ink-muted,#666); white-space: nowrap; &:disabled { opacity: .4; cursor: not-allowed; } &--save { background: var(--system-navbar,#0f4c81); color: #fff; border-color: transparent; } &--cancel { color: #dc2626; border-color: rgba(220,38,38,.3); } }
.toggle-check { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 500; color: var(--ink-muted,#666); cursor: pointer; input { cursor: pointer; accent-color: var(--system-navbar,#0f4c81); } }
.media-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.media-block { display: flex; flex-direction: column; gap: 8px; }
.media-label-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.upload-zone { height: 120px; border-radius: 10px; border: 2px dashed rgba(31,30,26,.2); background: #fafafa; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; cursor: pointer; color: var(--ink-muted,#aaa); overflow: hidden; transition: border-color .15s, background .15s; &:hover:not(.upload-zone--disabled) { border-color: var(--system-navbar,#0f4c81); background: #f0f5ff; } &--has { border-style: solid; border-color: rgba(31,30,26,.12); } &--disabled { background: #f0f0f0; cursor: not-allowed; border-color: rgba(31,30,26,.1); } }
.upload-hint { font-size: 12px; }
.upload-preview { width: 100%; height: 100%; object-fit: cover; }
.color-swatch-preview { width: 48px; height: 48px; border-radius: 50%; border: 3px solid rgba(255,255,255,.6); box-shadow: 0 2px 8px rgba(0,0,0,.2); }
.remove-btn { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; padding: 6px 13px; border-radius: 7px; border: 1.5px solid rgba(220,38,38,.3); background: #fef2f2; color: #dc2626; cursor: pointer; font-size: 12px; font-weight: 600; &:hover { background: #fee2e2; } }
.hidden-input { display: none; }
.color-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.color-block { display: flex; align-items: flex-start; gap: 14px; }
.color-swatch { width: 52px; height: 52px; border-radius: 10px; flex-shrink: 0; border: 2px solid rgba(31,30,26,.12); box-shadow: 0 2px 8px rgba(0,0,0,.1); margin-top: 22px; }
.color-fields { flex: 1; display: flex; flex-direction: column; gap: 6px; }
.color-label-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.color-input-row { display: flex; height: 40px; }
.hex-input { flex: 1; height: 100% !important; padding: 0 10px !important; border-radius: 8px 0 0 8px !important; border-right: 0 !important; font-size: 13px; font-family: monospace; }
.picker-inside { width: 60px; min-width: 60px; height: 100%; border-radius: 0 8px 8px 0; border: 1.5px solid rgba(31,30,26,.16); border-left: 0; padding: 0; background: #fff; cursor: pointer; overflow: hidden; &:disabled { opacity: .4; cursor: not-allowed; } &::-webkit-color-swatch-wrapper { padding: 0; } &::-webkit-color-swatch { border: 0; border-radius: 0 8px 8px 0; } }
.save-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 4px 0; }
.btn-save { display: inline-flex; align-items: center; gap: 8px; padding: 11px 24px; border-radius: 10px; border: none; cursor: pointer; background: var(--system-navbar,#0f4c81); color: #fff; font-size: 14px; font-weight: 700; &:hover { opacity: .88; } }
.save-success { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #15803d; }
.save-error { margin: 0; font-size: 13px; color: #dc2626; }
.fade-enter-active, .fade-leave-active { transition: opacity .3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
@media (max-width: 800px) { .fields-row, .media-grid, .color-grid { grid-template-columns: 1fr; } .settings-card { padding: 16px; } }

.report-mode-options { display: flex; gap: 12px; flex-wrap: wrap; }
.report-mode-card {
  flex: 1; min-width: 200px;
  border: 1.5px solid rgba(31,30,26,.1); border-radius: 10px; padding: 14px 16px;
  cursor: pointer; background: var(--bg,#fff); transition: border-color .15s, background .15s;
  display: flex; align-items: flex-start; gap: 10px;
  input[type="radio"] { display: none; }
  &--active { border-color: var(--system-navbar,#0f4c81); background: color-mix(in srgb, var(--system-navbar,#0f4c81) 6%, var(--bg,#fff)); }
  &:hover:not(&--active) { border-color: rgba(31,30,26,.25); }
}
.report-mode-body {
  display: flex; align-items: flex-start; gap: 12px;
  svg { flex-shrink: 0; color: var(--system-navbar,#0f4c81); margin-top: 1px; }
  div { display: flex; flex-direction: column; gap: 3px; }
  strong { font-size: 13px; font-weight: 700; color: var(--ink,#2c2b27); }
  span { font-size: 12px; color: var(--ink-muted,#5c5b57); line-height: 1.4; }
}
</style>
